import { observer } from 'mobx-react-lite';
import { useTranslationsContext } from 'store/translations';
import { useStoreContext } from 'store/app';

import { Button, CloseIcon } from 'components/core';
import { useLocation, useNavigate } from 'react-router';
import { CollectionCard } from 'components/cards/collectionCard';
import { UIEventHandler, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import './collectionPage.styl';
import { routes } from 'utils/router';
import { OverlayExchange } from './components/OverlayExchange';

export const CollectionPage = observer(() => {
    const { translate } = useTranslationsContext();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { catalogConfig, userState, isMob, logOperation, isCollectionFinished, isMainPartFinished, isShowResults } =
        useStoreContext();
    const [openOverlay, setOpenOverlay] = useState(false);
    const [selectedPresentId, setSelectedPresentId] = useState<number | null>(null);
    const [isTopBlurVisible, setIsTopBlurVisible] = useState(false);
    // const [isBottomBlurVisible, setIsBottomBlurVisible] = useState(false);
    const [scrollerRef, setScrollerRef] = useState<HTMLDivElement | null>();

    useEffect(() => {
        logOperation('collections', 'render');
    }, []);

    const checkBlurState = useCallback(() => {
        if (!scrollerRef) return;
        const { scrollTop } = scrollerRef;
        setIsTopBlurVisible(scrollTop > 2);
        // setIsBottomBlurVisible(Math.abs(scrollHeight - offsetHeight - scrollTop) > 2);
    }, [scrollerRef]);

    useLayoutEffect(() => {
        checkBlurState();
    }, [checkBlurState]);

    const onScroll: UIEventHandler<HTMLDivElement> = useCallback(() => {
        checkBlurState();
    }, [checkBlurState]);

    const onClose = () => {
        navigate(routes.main);
    };

    const { selectedSnake } = state || {};

    const catalogAmounts = userState?.collections
        ? userState?.collections.reduce((acc, x) => {
              acc[x.id] = x.snakes ? x.snakes.length : 0;
              return acc;
          }, {} as Record<string, number>)
        : null;

    const sorted = catalogAmounts
        ? catalogConfig?.collections.slice().sort((a, b) => {
              const aCount = catalogAmounts[a.id] || 0;
              const bcount = catalogAmounts[b.id] || 0;

              const aValue = aCount === 3 ? 0 : aCount === 0 ? 1 : 2;
              const bValue = bcount === 3 ? 0 : bcount === 0 ? 1 : 2;
              return bValue - aValue;
          })
        : catalogConfig?.collections.slice();

    let normalized = [...(sorted || [])];
    if (!isMob) {
        const halfedLength = Math.floor(normalized.length / 2);
        sorted?.forEach((item, index) => {
            if (index % 2 === 0) {
                normalized[index / 2] = item;
            } else {
                const additiveIndex = (index - 1) / 2;
                normalized[halfedLength + additiveIndex] = item;
            }
        });
    }

    const description = useMemo(() => {
        const text = isMainPartFinished
            ? isShowResults
                ? translate('collections.desc.results')
                : translate('collections.desc.afterFinish')
            : translate('collections.desc');

        if (isCollectionFinished) {
            return translate('collections.desc.completed') + ' ' + text;
        }

        return text;
    }, [isCollectionFinished, isMainPartFinished, isShowResults]);

    return (
        <div className="collection-page">
            <div className="collection-page-title">
                <span className="collection-page-title-text">{translate('collections.title')}</span>
                {selectedSnake ? (
                    <Button className="collection-page-title-button secondary" onClick={onClose}>
                        {translate('collections.next')}
                    </Button>
                ) : (
                    <CloseIcon onClick={onClose} />
                )}
            </div>
            <div className="collection-page-desc">{description}</div>
            <div className="collection-page-grid-wrapper">
                <div className="collection-page-grid-scroller" onScroll={onScroll} ref={setScrollerRef}>
                    <div className="collection-page-grid">
                        {normalized?.map((col) => (
                            <CollectionCard
                                collectionId={col.id}
                                key={col.id}
                                activeSnake={selectedSnake}
                                onPrizeModalClick={() => {
                                    setOpenOverlay(true);
                                    logOperation('collections', 'change_view', col.id);
                                }}
                                setSelectedPresentId={(id) => {
                                    setSelectedPresentId(id);
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className="collection-page-grid-top-blur" style={{ opacity: isTopBlurVisible ? 1 : 0 }} />
                {/* <div className="collection-page-grid-bottom-blur" style={{ opacity: isBottomBlurVisible ? 1 : 0 }} /> */}
            </div>
            {openOverlay && selectedPresentId && (
                <OverlayExchange presentId={selectedPresentId} onCancel={() => setOpenOverlay(false)} />
            )}
        </div>
    );
});
